import React, { Fragment, PureComponent } from 'react';
import { createPortal } from 'react-dom';
import queryString from 'query-string';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { get, throttle, debounce } from 'lodash';
import { Row, Col, Button, Alert } from 'reactstrap';
import generate from 'shortid';

import { EmployeeDocumentsTable, NoContent, Spinner, SearchBar, CenteredModal, SendForReminder } from '../../components';
import {
  GET_EMPLOYEE_DOCUMENTS, GET_COMPANIES, getEmployeeDocuments,
  getDocumentPreviewUrl, getSignUrl, toggleDrawer, getDocumentDownloadUrl,
  getCompanies, GET_USER_ROLES, setSelectedDocument, LOADING_PROP_TYPE,
  displayToaster, GET_DOCUMENT_DOWNLOAD_URL, DELETE_EMPLOYEE_DOCUMENT,
} from '../../modules';
import { SimpleSignDocuments, EditEmployeeDocument, UploadDocument } from '../';
import SignDocument from '../routes/SignDocument/SignDocument';
import { downloadFromUrl, convertBase64ToBlobUrl, checkRole } from '../../helpers';
import {
  FILTER_TABLE_DEBOUNCE, FETCH_TABLE_SEARCH_DEBOUNCE, ROLE_BASE_MANAGER,
  ROLE_BASE_USER, APPLICATION_ROOT, PENDING_STEP_NUMBER,
} from '../../constants';

class EmployeeDocuments extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      target: document.getElementById('employee-documents-ui'),
      nextPage: '',
      selectedCompanyId: get(window, 'asure.appSettings.selectedCompany.companyId'),
      selectedEmployeeId: get(window, 'asure.appSettings.selectedEmployee.employeeId'),
      paginating: true,
      currentDocDownloadId: null,
      drawerMode: '',
      title: '',
      docFilter: '',
      filtering: false,
      searching: false,
      latestSearch: '',
      fetchActive: '',
      unfilteredNextPage: '',
      isModalOpen: false,
      essPageQueryParamPresent: queryString.parse(window.location.search).menu === 'ESS',

    };
    this.throttledScroll = throttle(this.handleScroll, 500);
    this.showSendReminderModal = this.showSendReminderModal.bind(this);
  }

  componentDidMount() {
    const {
      target, nextPage: next, selectedCompanyId, selectedEmployeeId, essPageQueryParamPresent,
    } = this.state;
    const { roles } = this.props;
    window.addEventListener('scroll', this.throttledScroll);
    const employeeViewingESS = roles.length > 0 ? roles.includes(ROLE_BASE_USER) && essPageQueryParamPresent : true;
    if (target && (selectedEmployeeId || !employeeViewingESS)) {
      this.loadData(next);
    }
    if (!selectedCompanyId) {
      this.props.getCompanies();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if ((!prevState.selectedCompanyId && this.state.selectedCompanyId) ||
        (!prevState.selectedEmployeeId && this.state.selectedEmployeeId)) {
      this.loadData();
    }

    if (
      this.state.target &&
      prevProps.roles.length === 0 &&
        this.props.roles.length > 0 &&
        !(this.props.roles.includes(ROLE_BASE_USER) &&
        this.state.essPageQueryParamPresent)
    ) {
      this.loadData();
    }

    const { fileDownload } = this.props;
    if (this.state.target &&
      ((!prevProps.fileDownload && fileDownload) ||
      (prevProps.fileDownload && prevProps.fileDownload.url !== fileDownload.url))
    ) {
      const { url, fileName } = fileDownload;
      const fileUrl = url && url.split(',')[0].split(';').pop() === 'base64' ? convertBase64ToBlobUrl(url) : url;
      downloadFromUrl(fileUrl, fileName);
    }
    if (this.state.fetchActive !== this.state.latestSearch &&
        this.props.loading.currentRequests[GET_EMPLOYEE_DOCUMENTS].includes(this.state.latestSearch)) {
      this.setFetchActive();
    }
    if (this.state.filtering &&
        this.state.fetchActive === this.state.latestSearch &&
        !this.props.loading.currentRequests[GET_EMPLOYEE_DOCUMENTS].includes(this.state.latestSearch)) {
      this.unsetFiltering();
    }
    if (this.props.loading[DELETE_EMPLOYEE_DOCUMENT] === 'complete' && prevProps.loading[DELETE_EMPLOYEE_DOCUMENT] === 'loading') {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_DELETED" />,
        'app',
        5000,
        true,
      ]);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.throttledScroll);
  }

  getFetchType = () => {
    const { selectedCompanyId, selectedEmployeeId } = this.state;
    let type = 'tenant';
    if (selectedCompanyId && !selectedEmployeeId) {
      type = 'company';
    }
    if (selectedCompanyId && selectedEmployeeId) {
      type = 'employee';
    }
    return type;
  };

  setFetchActive() {
    this.setState({ fetchActive: this.state.latestSearch });
  }

  unsetFiltering() {
    this.setState({ filtering: false });
  }

  dataRetrievalStatus = (actionName, status) => {
    const { loading } = this.props;
    return loading[actionName] === status;
  };

  // Load more documents when the user has reached the end of the page
  handleScroll = () => {
    const { next } = this.props.docs;
    if (!next) {
      this.setState({ paginating: false });
      return;
    }
    if (window.innerHeight + document.documentElement.scrollTop > document.documentElement.offsetHeight - 100) {
      const unfilteredNext = this.state.docFilter ? this.state.unfilteredNextPage : next;
      this.setState(() => ({
        nextPage: next,
        paginating: true,
        searching: false,
        unfilteredNextPage: unfilteredNext,
      }), () => this.loadData(next));
    }
  };

  loadData = (next) => {
    this.props.getEmployeeDocuments(this.getFetchType(), next);
  };

  uploadDocument = () => {
    const { state } = this;
    this.setState({
      ...state,
      drawerMode: 'upload',
    });
    this.props.toggleDrawer(true);
  };

  editDocument = (documentId) => {
    const { docs: { results: documents }, roles } = this.props;
    const { essPageQueryParamPresent } = this.state;
    const document = documents.filter(doc => doc.id === documentId)[0];
    this.props.setSelectedDocument(document);
    const pendingDoc = document && document.status && document.status.stepNumber === PENDING_STEP_NUMBER;
    const isDocProcessing = document.status.isProcessing === true;
    const employeeViewingESS = roles.length > 0 && roles.includes(ROLE_BASE_USER) && essPageQueryParamPresent;

    // Prevent edit functionality while we're loading data
    if (this.props.getDocsLoading === 'loading') return;
    const { state } = this;
    if (pendingDoc && employeeViewingESS && document.isHelloSignDocument) {
      this.setState({
        ...state,
        drawerMode: 'sign',
      }, () => {
        this.props.getSignUrl(documentId);
        this.props.toggleDrawer(true);
      });
    } else if (pendingDoc && employeeViewingESS && document.isEsignatureDocument && !document.isHelloSignDocument) {
      this.setState({
        ...state,
        drawerMode: 'addSignature',
      }, () => {
        this.props.getDocumentPreviewUrl('employee', documentId);
        this.props.toggleDrawer(true);
      });
    } else {
      this.setState({
        ...state,
        drawerMode: 'edit',
      }, () => {
        if (!pendingDoc && !isDocProcessing) {
          this.props.getDocumentPreviewUrl(this.getFetchType(), documentId);
        }
        this.props.toggleDrawer(true);
      });
    }
  };

  toggleSendReminderModal(isOpen) {
    this.setState({ isModalOpen: !isOpen });
  }

  showSendReminderModal(templateId) {
    const { docs: { results: documents } } = this.props;
    const document = documents.filter(doc => doc.id === templateId)[0];

    this.setState({ isModalOpen: true }, () => {
      this.props.setSelectedDocument(document);
    });
  }

  searchDocs = (query, requestId) => {
    if (query) {
      this.props.getEmployeeDocuments(this.getFetchType(), '', query, requestId);
    } else {
      this.loadData(this.state.unfilteredNextPage);
    }
  }

  debounceSearchDocs = debounce(this.searchDocs, (FETCH_TABLE_SEARCH_DEBOUNCE - FILTER_TABLE_DEBOUNCE));

  handleSearch = (query) => {
    if (query !== this.state.docFilter) {
      const requestId = generate();
      this.setState({
        docFilter: query,
        paginating: true,
        searching: !!query,
        filtering: !!query,
        latestSearch: requestId,
      });
      this.debounceSearchDocs(query, requestId);
    }
  }

  debounceHandleSearch = debounce(this.handleSearch, FILTER_TABLE_DEBOUNCE);

  handleDownload = (documentId) => {
    const { fileName } = this.props.docs.results.filter(doc => doc.id === documentId)[0];
    this.props.getDocumentDownloadUrl(this.getFetchType(), documentId, fileName);
    const { state } = this;
    this.setState({
      ...state,
      currentDocDownloadId: documentId,
    });
  };

  buildComponentContent() {
    const {
      docs: {
        results: documents,
      },
      loading,
      getDocsLoading,
      downloadUrlLoading,
      isManagerOrAbove,
      roles,
    } = this.props;
    const {
      paginating, currentDocDownloadId,
      drawerMode, docFilter, filtering,
      searching, selectedEmployeeId,
      essPageQueryParamPresent,
    } = this.state;
    let drawerContent;
    if (drawerMode === 'upload') {
      drawerContent = <UploadDocument />;
    } else if (drawerMode === 'edit') {
      drawerContent = <EditEmployeeDocument />;
    } else if (drawerMode === 'sign') {
      drawerContent = <SignDocument />;
    } else if (drawerMode === 'addSignature') {
      drawerContent = <SimpleSignDocuments />;
    }

    const employeeViewingESS = roles.length > 0 && roles.includes(ROLE_BASE_USER) && essPageQueryParamPresent;
    if (this.dataRetrievalStatus(GET_EMPLOYEE_DOCUMENTS, 'complete') && documents.length === 0) {
      return (
        <Fragment>
          <Col sm={12}>
            <NoContent
              heading="NO_DOCUMENTS_TO_DISPLAY"
              bodyText="ADD_A_DOCUMENT_TO_GET_STARTED"
              buttonText="ADD_DOCUMENT"
              onClick={() => this.uploadDocument()}
              disabled={this.dataRetrievalStatus(GET_COMPANIES, 'loading')}
            />
          </Col>
          <UploadDocument />
        </Fragment>
      );
    } else if (!selectedEmployeeId && employeeViewingESS) {
      return (
        <Row>
          <Col sm={12}>
            <Alert color="primary">
              <h4><FormattedMessage id="SELECT_AN_EMPLOYEE" /></h4>
              <p><FormattedMessage id="SELECT_AN_EMPLOYEE_VERBIAGE" /></p>
            </Alert>
          </Col>
        </Row>
      );
    }
    return (
      <Fragment>
        <Row className="pb-3 ee-docs-toolbar">
          <Col
            xs={{ size: 12 }}
            sm={{ size: 6 }}
            md={{ size: 5 }}
            lg={{ size: 4 }}
            xl={{ size: 3 }}
            className="text-left"
          >
            <SearchBar
              name="docFilter"
              placeholder="Search Employee Documents"
              keyUpHandler={this.debounceHandleSearch.bind(this)}
              filtering={filtering}
              loading={getDocsLoading === 'loading'}
              searching={searching}
            />
          </Col>
          <Col
            xs={{ size: 12, offset: 0 }}
            sm={{ size: 4, offset: 2 }}
            md={{ size: 3, offset: 4 }}
            lg={{ size: 3, offset: 5 }}
            xl={{ size: 2, offset: 7 }}
            className="text-right my-auto"
          >
            <Button
              onClick={() => this.uploadDocument()}
              color="primary"
              block
              disabled={(loading[GET_USER_ROLES] === '' || loading[GET_USER_ROLES] === 'loading')}
              data-cy="add-document"
            >
              <FormattedMessage id="ADD_DOCUMENT" />
            </Button>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <div className="bg-white border-grey border p-4 w-100 d-flex flex-column">
              <EmployeeDocumentsTable
                data={documents}
                rowAction={this.editDocument}
                key={documents}
                downloadAction={this.handleDownload}
                getDocsLoading={getDocsLoading}
                downloadUrlLoading={downloadUrlLoading}
                currentDocDownloadId={currentDocDownloadId}
                docFilter={docFilter}
                filtering={filtering}
                isManagerOrAbove={isManagerOrAbove}
                toggleSendReminderModal={this.showSendReminderModal}
              />
            </div>
          </Col>
          <Col sm={12}>
            <div className="w-100 d-flex justify-content-center">
              <h6 className="d-flex justify-content-center flex-row p-3">
                {
                  paginating
                  && this.dataRetrievalStatus(GET_EMPLOYEE_DOCUMENTS, 'loading')
                  && (
                    this.state.searching ?
                      null
                      :
                      <Fragment><Spinner className="mr-1" /><FormattedMessage id="LOADING_MORE_DOCUMENTS" />...</Fragment>
                  )
                }
              </h6>
            </div>
          </Col>
        </Row>
        {drawerContent}
      </Fragment>
    );
  }

  render() {
    const { target } = this.state;
    const component = (
      <div className="pt-4 h-100">
        <CenteredModal
          style={{ margin: '10rem auto', maxWidth: '600px' }}
          isOpen={this.state.isModalOpen}
          parentselector={APPLICATION_ROOT}
          toggle={() => this.setState({ isModalOpen: !this.state.isModalOpen })}
          backdrop
        >
          <SendForReminder onClose={() => this.toggleSendReminderModal(true)} />
        </CenteredModal>
        <Row>
          <Col xs={12}>
            <h1 className="h3"><FormattedMessage id="EMPLOYEE_DOCUMENTS" /></h1>
          </Col>
        </Row>
        {this.buildComponentContent()}
      </div>
    );
    if (target) {
      return createPortal(component, target);
    }
    return null;
  }
}

EmployeeDocuments.defaultProps = {
  toggleDrawer: null,
  loading: {},
  downloadUrlLoading: '',
  getDocsLoading: '',
  fileDownload: null,
  isManagerOrAbove: false,
  roles: [],
};


EmployeeDocuments.propTypes = {
  loading: LOADING_PROP_TYPE,
  downloadUrlLoading: PropTypes.string,
  getDocsLoading: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  docs: PropTypes.shape({
    next: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      fileName: PropTypes.string,
      category: PropTypes.string,
      uploadDate: PropTypes.string,
      esignDate: PropTypes.string,
      companyName: PropTypes.string,
      employeeName: PropTypes.string,
      employeeCode: PropTypes.string,
      isEsignatureDocument: PropTypes.bool,
      isHelloSignDocument: PropTypes.bool,
      status: PropTypes.shape({
        name: PropTypes.string,
        priority: PropTypes.number,
        stepNumber: PropTypes.number,
      }),
    })),
  }).isRequired,
  getDocumentPreviewUrl: PropTypes.func.isRequired,
  getEmployeeDocuments: PropTypes.func.isRequired,
  getDocumentDownloadUrl: PropTypes.func.isRequired,
  setSelectedDocument: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func,
  fileDownload: PropTypes.shape({
    url: PropTypes.string,
    filename: PropTypes.string,
  }),
  isManagerOrAbove: PropTypes.bool,
  getCompanies: PropTypes.func.isRequired,
  displayToaster: PropTypes.func.isRequired,
  getSignUrl: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const { resources: { docs: { employeeDocs, fileDownload } }, appState: { loading }, user: { roles } } = state;
    return {
      loading,
      downloadUrlLoading: loading[GET_DOCUMENT_DOWNLOAD_URL],
      getDocsLoading: loading[GET_EMPLOYEE_DOCUMENTS],
      docs: employeeDocs,
      fileDownload,
      isManagerOrAbove: checkRole(roles, ROLE_BASE_MANAGER),
      roles,
    };
  },
  {
    getDocumentPreviewUrl,
    getEmployeeDocuments,
    getDocumentDownloadUrl,
    getSignUrl,
    toggleDrawer: open => toggleDrawer(open),
    getCompanies,
    setSelectedDocument,
    displayToaster: args => displayToaster(...args),
  }
)(hot(module)(EmployeeDocuments));



// WEBPACK FOOTER //
// ./src/containers/EmployeeDocuments/EmployeeDocuments.jsx